import {
  Observation,
  ObservationCreate,
  ObservationUpdate,
  ObservationFilters,
  PaginatedResponse,
  BulkObservationRequest,
  BulkObservationResponse
} from '../../types/api';
import { apiClient } from './client';

const BASE_URL = '/observations';

export const observationsApi = {
  /**
   * Get paginated list of observations with optional filters.
   *
   * The API returns the envelope `{items, total}`. We derive `page`,
   * `size`, and `pages` from skip/limit/total so the UI's
   * PaginatedResponse<T> shape stays stable across callers.
   *
   * The Array.isArray branch is transitional: previously the API
   * returned a bare list and the service fabricated `pages: 1`, which
   * structurally hid the pager. Kept as a fallback so a downgrade or
   * proxy that replays the old shape does not break the page; it just
   * disables pagination as it did before.
   */
  async getObservations(filters?: ObservationFilters): Promise<PaginatedResponse<Observation>> {
    const params = new URLSearchParams();

    if (filters?.skip !== undefined) params.append('skip', filters.skip.toString());
    if (filters?.limit !== undefined) params.append('limit', filters.limit.toString());
    if (filters?.child_id) params.append('child_id', filters.child_id);
    if (filters?.category) params.append('category', filters.category);
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.is_milestone !== undefined) params.append('is_milestone', filters.is_milestone.toString());
    if (filters?.is_concern !== undefined) params.append('is_concern', filters.is_concern.toString());

    const response = await apiClient.get(`${BASE_URL}?${params.toString()}`);

    const skip = filters?.skip ?? 0;
    const limit = filters?.limit ?? 20;

    let items: Observation[];
    let total: number;
    if (Array.isArray(response.data)) {
      items = response.data;
      total = items.length;
    } else {
      items = response.data.items ?? [];
      total = response.data.total ?? items.length;
    }

    const size = items.length;
    const page = Math.floor(skip / limit) + 1;
    const pages = Math.max(1, Math.ceil(total / limit));
    return { items, total, page, size, pages };
  },

  /**
   * Get a specific observation by ID
   */
  async getObservation(id: string): Promise<Observation> {
    const response = await apiClient.get(`${BASE_URL}/${id}`);
    return response.data;
  },

  /**
   * Create a new observation
   */
  async createObservation(data: ObservationCreate): Promise<Observation> {
    const response = await apiClient.post(BASE_URL, data);
    return response.data;
  },

  /**
   * Update an existing observation
   */
  async updateObservation(id: string, data: ObservationUpdate): Promise<Observation> {
    const response = await apiClient.put(`${BASE_URL}/${id}`, data);
    return response.data;
  },

  /**
   * Delete an observation
   */
  async deleteObservation(id: string): Promise<void> {
    await apiClient.delete(`${BASE_URL}/${id}`);
  },

  /**
   * Publish an observation
   */
  async publishObservation(id: string): Promise<{ message: string }> {
    const response = await apiClient.post(`${BASE_URL}/${id}/publish`);
    return response.data;
  },

  /**
   * Unpublish an observation
   */
  async unpublishObservation(id: string): Promise<{ message: string }> {
    const response = await apiClient.post(`${BASE_URL}/${id}/unpublish`);
    return response.data;
  },

  /**
   * Verify an observation (admin/supervisor only)
   */
  async verifyObservation(id: string): Promise<{ message: string }> {
    const response = await apiClient.post(`${BASE_URL}/${id}/verify`);
    return response.data;
  },

  /**
   * Get observations for a specific child
   */
  async getObservationsByChild(childId: string, filters?: Omit<ObservationFilters, 'child_id'>): Promise<PaginatedResponse<Observation>> {
    return this.getObservations({ ...filters, child_id: childId });
  },

  /**
   * Get observations by current user (teacher).
   *
   * The API scopes "my observations" via the listing-scope rule on
   * `current_user.id`, not via a query param -- see
   * `sprout/api/routes/observations.py`.
   */
  async getMyObservations(filters?: ObservationFilters): Promise<PaginatedResponse<Observation>> {
    return this.getObservations(filters);
  },

  /**
   * Get recent observations (last 7 days)
   */
  async getRecentObservations(limit: number = 10): Promise<PaginatedResponse<Observation>> {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    return this.getObservations({
      date_from: sevenDaysAgo.toISOString().split('T')[0],
      limit,
    });
  },

  /**
   * Get milestone observations
   */
  async getMilestoneObservations(filters?: Omit<ObservationFilters, 'is_milestone'>): Promise<PaginatedResponse<Observation>> {
    return this.getObservations({ ...filters, is_milestone: true });
  },

  /**
   * Get concern observations
   */
  async getConcernObservations(filters?: Omit<ObservationFilters, 'is_concern'>): Promise<PaginatedResponse<Observation>> {
    return this.getObservations({ ...filters, is_concern: true });
  },

  /**
   * Get observations that require follow-up
   */
  async getFollowUpObservations(filters?: ObservationFilters): Promise<Observation[]> {
    const response = await this.getObservations(filters);
    return response.items.filter(obs => obs.follow_up_required);
  },

  /**
   * Get observations by category
   */
  async getObservationsByCategory(category: string, filters?: Omit<ObservationFilters, 'category'>): Promise<PaginatedResponse<Observation>> {
    return this.getObservations({ ...filters, category });
  },

  /**
   * Get available observation categories
   */
  async getCategories(): Promise<string[]> {
    const response = await apiClient.get(`${BASE_URL}/categories`);
    return response.data;
  },

  /**
   * Get available learning areas
   */
  async getLearningAreas(): Promise<string[]> {
    const response = await apiClient.get('/curriculum/areas');
    return response.data;
  },

  /**
   * Create the same observation for multiple children at once.
   *
   * The backend accepts a shared `observation_data` payload and a list of
   * `child_ids`, returning separate created/failed results per child.
   */
  async bulkCreate(request: BulkObservationRequest): Promise<BulkObservationResponse> {
    const response = await apiClient.post(`${BASE_URL}/bulk`, request);
    return response.data;
  },

  /**
   * Ask the local LLM (via Ollama on the API server) to flag specific
   * issues in an observation draft. Always returns a list (possibly empty)
   * -- on Ollama failure the server returns [] rather than 5xx so the
   * editor can degrade gracefully.
   */
  async suggestImprovements(
    request: ObservationSuggestRequest,
  ): Promise<ObservationSuggestResponse> {
    const response = await apiClient.post(`${BASE_URL}/suggest`, request);
    return response.data;
  },
};

// ── AI assist types ─────────────────────────────────────────────────────

export type SuggestionSeverity = 'minor' | 'moderate';

/** Stable issue codes the backend may return; kept open-ended for forward
 * compatibility (new issue types ship without a frontend deploy).
 *
 * `outcome_alignment` only appears in targeted mode (a learning_outcome_id
 * was passed in the request) -- it flags observations that don't actually
 * evidence the tagged CAPS outcome.
 */
export type SuggestionIssueCode =
  | 'vague_phrasing'
  | 'judgmental_language'
  | 'emotional_inference'
  | 'missing_evidence'
  | 'length'
  | 'outcome_alignment'
  | (string & {});

export interface ObservationSuggestion {
  issue: SuggestionIssueCode;
  /** Verbatim excerpt from the user's text that the model wants to change. */
  evidence: string;
  /** Suggested replacement / addition. */
  fix: string;
  severity: SuggestionSeverity;
}

/** Auto-suggested CAPS outcome the LLM thinks the observation evidences.
 * Only populated when the request omits `learning_outcome_id` (discovery
 * mode). The server has already mapped `code` to `outcome_id` so the UI
 * can persist the link without a follow-up lookup. */
export interface SuggestedOutcome {
  outcome_id: string;
  code: string;
  description: string;
  rationale: string;
  confidence: 'high' | 'medium' | 'low';
}

export interface ObservationSuggestRequest {
  text: string;
  child_age?: number;
  /** When provided, the LLM evaluates the observation against this
   * specific outcome (targeted mode). When omitted, the LLM also returns
   * suggested_outcomes for the teacher to pick from (discovery mode). */
  learning_outcome_id?: string;
}

export interface ObservationSuggestResponse {
  suggestions: ObservationSuggestion[];
  suggested_outcomes: SuggestedOutcome[];
}
